import React from 'react'
import { CategoryRow, CategoryDestinationLevel, CategoryRowItem } from '@vp/ep-tiles'
import { stylesheet as categoryTileStylesheet } from '@vp/ep-tiles/category-tile.css'
import { stylesheet as categoryRowStylesheet } from '@vp/ep-tiles/category-row.css'
import { useAppContext } from '../state/AppContext'
import { trackTemplateTileClickEvent } from '../utils/tracking/tracking'
import { Gallery } from '../types/Gallery'
import { TemplateCollectionType } from '../types/TemplateConfiguration'
import { Translated } from '../types/Global'
import { getGalleryUrl } from '../utils/gallery/gallery'
import { useStyles } from '@vp/ubik-context'
import { DebugPageSections } from './debugComponents/DebugPageSections'
import { Visible } from '@vp/swan'
import CloudinaryUtil from '../utils/cloudinaryUtil/cloudinaryUtil'
import { CLOUDINARY_TRANSFORM_TOKENS } from './DesignTemplate/constants'

interface CategoryRowWrapperProps {
  configuration: TemplateCollectionType
  galleries?: Gallery[] | undefined
  nextSectionId?: string | undefined
  nextSectionTitle?: Translated | undefined
  sectionNumber?: number
}

const CategoryRowWrapper: React.FC<CategoryRowWrapperProps> = ({ configuration, galleries }) => {
  useStyles(categoryTileStylesheet)
  useStyles(categoryRowStylesheet)
  const { locale, tenant, sidebarEnabled, merchandisingCategoryId } = useAppContext()
  const { title, items, sectionTypeCount } = configuration

  const tiles = items
    .map((item) => {
      const link = item.link as any
      const { cloudinaryImage, text, targetUrl, actionId, actionFilters } = link

      const url: string =
        getGalleryUrl({
          ...(galleries && { galleries }),
          ...(actionFilters && { actionFilters }),
          actionId,
          locale
        }) || targetUrl

      if (!cloudinaryImage?.cloudinaryUrl || !targetUrl) {
        return null
      }

      const transformedImageURL = CloudinaryUtil.applyImageTransformation(
        cloudinaryImage.cloudinaryUrl,
        CLOUDINARY_TRANSFORM_TOKENS
      )

      return {
        categoryId: actionId,
        categoryTileTitle: text,
        categoryTileImage: {
          src: transformedImageURL,
          altTextOverride: text
        },
        categoryTileUrl: url
      } satisfies CategoryRowItem
    })
    .filter((tile) => tile !== null)

  if (!tiles.length) {
    return null
  }

  const handleTrackOnClick = (tile: CategoryRowItem) => {
    const index = tiles.findIndex((t) => t.categoryTileUrl === tile.categoryTileUrl)

    trackTemplateTileClickEvent({
      categoryId: merchandisingCategoryId,
      destinationUrl: tile.categoryTileUrl,
      pageZone: 'Category Row',
      ctaValue: `Tile ${index + 1}`,
      index: index + 1,
      itemsCount: tiles.length,
      sectionTypeCount: sectionTypeCount ?? 1
    })
  }
  return (
    <>
      <Visible xs>
        <CategoryRow
          categoryDestinationLevel={CategoryDestinationLevel.ROOT}
          title={title?.value}
          tiles={tiles}
          locale={locale}
          tenant={tenant}
          trackOnClick={handleTrackOnClick}
        />
      </Visible>
      <Visible sm md lg xl>
        <CategoryRow
          categoryDestinationLevel={
            sidebarEnabled ? CategoryDestinationLevel.NEXT : CategoryDestinationLevel.ROOT
          }
          title={title?.value}
          tiles={tiles}
          locale={locale}
          tenant={tenant}
          trackOnClick={handleTrackOnClick}
        />
      </Visible>
      <DebugPageSections rawSectionData={configuration} resolvedSectionData={tiles} />
    </>
  )
}

export default CategoryRowWrapper
